import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

export default function RefundPage() {
  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="pt-20 pb-16">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
            <h1 className="text-3xl font-bold text-gray-900 mb-8">Refund Policy</h1>
            
            <div className="prose prose-lg max-w-none text-gray-700 space-y-6">
              <p className="text-sm text-gray-500 mb-8">Last updated: January 2025</p>
              
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 mb-8">
                <h3 className="text-lg font-semibold text-blue-900 mb-2">Important Notice</h3>
                <p className="text-blue-800">A product key can be returned only 14 days after the purchase has been made. Within these 14 days, you can only receive a refund in specific circumstances outlined below.</p>
              </div>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Eligible Refund Scenarios</h2>
                <p>You may be eligible for a refund in the following two cases:</p>
                
                <div className="space-y-4 mt-6">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-900 mb-2">1. Before Product Code is Viewed</h3>
                    <p className="text-green-800">If you have not yet displayed or viewed the product key, you can request a refund within 14 days of purchase.</p>
                  </div>
                  
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <h3 className="font-semibold text-green-900 mb-2">2. Invalid or Previously Used Product Code</h3>
                    <p className="text-green-800">If there's an issue with the product code itself (it is either invalid or previously used), you can request a refund regardless of whether you've viewed the key.</p>
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Refund Process for Non-Displayed Keys</h2>
                <p>If you want a refund for a key that has not been displayed:</p>
                <ol className="list-decimal pl-6 space-y-2">
                  <li>Log into your account and navigate to your order history</li>
                  <li>Find the order containing the product you wish to refund</li>
                  <li>Click on "Request Refund" before viewing the product key</li>
                  <li>Provide a reason for the refund request</li>
                  <li>Submit the request for review</li>
                </ol>
                <p className="mt-4 text-sm text-gray-600">Refunds for non-displayed keys are typically processed within 3-5 business days.</p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Issues with Product Keys</h2>
                <p>If you face an issue with your purchased key (invalid, already used, or activation problems), please contact our Customer Support Team immediately with the following information:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Order number and purchase date</li>
                  <li>Product name and key (if applicable)</li>
                  <li>Screenshot of the error message</li>
                  <li>Platform where you attempted activation</li>
                  <li>Detailed description of the issue</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Already Displayed Keys</h2>
                <div className="bg-red-50 border border-red-200 rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-2">Important Limitation</h3>
                  <p className="text-red-800">If you have already displayed and viewed the product key, and you simply changed your mind about the purchase, <strong>a refund is unfortunately not possible</strong>.</p>
                  <p className="text-red-800 mt-2">We are a digital key marketplace, and our sellers do not have technical means to revoke or deactivate a viewed key. This policy protects both buyers and sellers in the digital marketplace.</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Refund Methods</h2>
                <p>Approved refunds will be processed using the same payment method used for the original purchase:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li><strong>Credit/Debit Cards:</strong> 3-7 business days</li>
                  <li><strong>PayPal:</strong> 1-3 business days</li>
                  <li><strong>Digital Wallets:</strong> 1-5 business days</li>
                  <li><strong>Bank Transfers:</strong> 5-10 business days</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Exceptions and Special Cases</h2>
                <p>Certain products may have different refund policies:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Pre-order items may have extended refund periods</li>
                  <li>Subscription services follow their own cancellation terms</li>
                  <li>Gift cards and store credit are generally non-refundable</li>
                  <li>Products purchased with promotional discounts may have restrictions</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">How to Contact Support</h2>
                <p>For refund requests or issues with your purchase, contact our Customer Support Team:</p>
                <div className="bg-gray-50 p-4 rounded-lg mt-4">
                  <p><strong>Email:</strong> support@gamestore.com</p>
                  <p><strong>Live Chat:</strong> Available 24/7 on our website</p>
                  <p><strong>Phone:</strong> +1 (555) 123-4567</p>
                  <p><strong>Support Hours:</strong> Monday - Sunday, 24/7</p>
                </div>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-900 mb-4">Dispute Resolution</h2>
                <p>If you're not satisfied with our refund decision, you may:</p>
                <ul className="list-disc pl-6 space-y-2">
                  <li>Request a review by our senior support team</li>
                  <li>Provide additional evidence for your case</li>
                  <li>Contact your payment provider for a chargeback (as a last resort)</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
